<template>
  <vs-card v-if="job" :to="linkTo" :class="{ 'grey-out': job.is_archived }">
    <div>
      <v-row align="center">
        <v-col v-if="hasNoLineItems" class="shrink pa-0 pl-3">
          <v-tooltip bottom>
            <template v-slot:activator="{ on, attrs }">
              <v-icon small color="red" v-bind="attrs" v-on="on">mdi-alert-outline</v-icon>
            </template>
            <span>This job has no line items</span>
          </v-tooltip>
        </v-col>
        <v-col>
          <vs-text> {{ jobTitle }}</vs-text>
        </v-col>
        <v-col class="shrink">
          <v-icon small :color="priority?.color">
            {{ priority?.icon }}
          </v-icon>
        </v-col>
        <v-col class="shrink">
          <job-status-chip :status="finalStatus" />
        </v-col>
        <v-col :class="['shrink', { 'is-visible': showCheckbox }]">
          <v-checkbox v-model="selected" hide-details class="ma-0 pa-0" @click.prevent />
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <vs-heading>{{ job.activity_name }}</vs-heading>
        </v-col>
      </v-row>
      <v-row dense v-if="showAssignedToServiceProviderName && assignedToServiceProviderName">
        <v-col>
          <vs-text>Assigned to {{ assignedToServiceProviderName }}</vs-text>
        </v-col>
      </v-row>
      <v-row dense>
        <v-col>
          <vs-text type="caption">{{ taskText }}</vs-text>
        </v-col>
      </v-row>
    </div>

    <v-row align="center">
      <v-col class="shrink pr-4">
        <v-icon color="grey darken-2">calendar_today</v-icon>
      </v-col>

      <v-col>
        <vs-text type="overline">Schedule</vs-text>
        <vs-text>{{ scheduleTime }}</vs-text>
      </v-col>
    </v-row>
    <job-card-locations-detail
      :inventory="inventory"
      :destination-location-name="job.destination_name"
    />
    <template v-if="showLineItems">
      <v-divider />
      <line-items-card
        :job-id="props.job.id"
        :job-approval-status="props.job.approval_status"
        :assigned-to-service-provider-id="props.job.assigned_service_provider_id"
        :hub-id="props.job.hub_id"
        readonly
        :show-action-button="false"
        @line-item-count-changed="handleLineItemCountChanged"
      />
    </template>
  </vs-card>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import JobStatusChip from '@/components/tickets/JobStatusChip.vue'
import { NO_APPROVAL } from '@/models/job'
import JobCardLocationsDetail from '@/components/jobs/JobCardLocationsDetail.vue'
import { formatTimeRange } from '@/utils/date-formatting'
import moment from 'moment'
import { priorities } from '@/utils/constants'
import type { RouterLinkProps } from 'vue-router/types/router'
import { useStore } from 'vuex-composition-helpers'
import LineItemsCard from '@/components/jobs/line-items/LineItemsCard.vue'
import VsCard from '@/components/vision/VsCard.vue'
import VsText from '@/components/vision/VsText.vue'
import VsHeading from '@/components/vision/VsHeading.vue'
import Company from '@/models/company'
import { JobSearchResponse } from '@/models/types/job-search-response'
import { useInventoryTypes } from '@/composables/inventory-types/useInventoryTypes'

type RouterLinkTo = RouterLinkProps['to']

const props = defineProps<{
  job: JobSearchResponse
  linkTo: RouterLinkTo
  showCheckbox: boolean
  isSelected: boolean
  showLineItems?: boolean
  showAssignedToServiceProviderName?: boolean
}>()

const store = useStore()

const lineItemCount = ref<number | undefined>(undefined)

const hasNoLineItems = computed(() => {
  if (!props.showLineItems) return false
  return lineItemCount.value !== undefined && lineItemCount.value === 0
})

function handleLineItemCountChanged(count: number) {
  lineItemCount.value = count
}

const emit = defineEmits<{
  (e: 'selected', selectedJob: JobSearchResponse)
  (e: 'deselected', unselectedJob: JobSearchResponse)
}>()

const company = computed(() => {
  return store.getters['companies/getByChildHubId'](props.job?.hub_id)
})
const jobTitle = computed(() => `Job #${props.job?.number} · ${company.value?.shortCode}`)
const getCompanyByOrgId = computed<(orgId: string | null) => Company>(
  () => store.getters['companies/getByOrganizationId'],
)
const assignedToServiceProviderName = computed(
  () => getCompanyByOrgId.value(props.job.assigned_service_provider_id)?.name,
)

const priority = computed(() => priorities.find((p) => p.name === props.job.priority))

const selected = computed({
  get() {
    return props.isSelected
  },
  set(selectedValue) {
    if (selectedValue) {
      selectJob(props.job)
    } else {
      deselectJob(props.job)
    }
  },
})

const scheduleTime = computed(() => {
  const startTime = props.job.requested_start && moment.utc(props.job.requested_start).local()
  const endTime = props.job.requested_end && moment.utc(props.job.requested_end).local()
  return formatTimeRange(startTime, endTime)
})

const finalStatus = computed(() =>
  props.job.approval_status !== null && props.job.approval_status !== NO_APPROVAL
    ? props.job.approval_status
    : props.job.status.trim(),
)

const { getInventoryTypeById } = useInventoryTypes()

const inventory = computed(() => {
  return props.job.inventory_requirements.map((inv) => ({
    name: getInventoryTypeById(inv.inventory_type_id)?.name,
    inventoryTypeId: inv.inventory_type_id,
    inventorySubTypeName: inv.name,
    inventorySubTypeId: inv.inventory_sub_type_id,
    quantity: inv.quantity,
    pickupLocationId: inv.pickup_location_id,
    pickupLocationName: inv.pickup_location_name,
    inventoryId: 0, // Not used
    position: inv.position,
    assetConditions: [], // Not used
  }))
})

const taskText = computed(() => {
  const assignment = props.job.assigned_resource_name || 'Unassigned'
  return `${props.job.resource_type_name} · ${assignment}`
})

function selectJob(job: JobSearchResponse) {
  emit('selected', job)
}

function deselectJob(job: JobSearchResponse) {
  emit('deselected', job)
}
</script>

<style scoped>
.grey-out {
  opacity: 0.6;
}
</style>
